import { createReducer } from '../../store.model';
import {
    checkMetricIdInMultiple,
    createCommitmentGoalLookup,
} from '../pillar-one/pillar-one.function';

import { determineAllowedActions } from './determine-allowed-actions.function';
import { LocationView } from './location-view.model';
import {
    ActionType,
    Reload,
    ReloadCancel,
    ReloadResult,
    SelectMetric,
    SetActive,
    SetCategoryOpenState,
    SetCategoryOpenStateForObservations,
    SetStatuses,
} from './pillar-two.action';
import {
    categorizeCommitments,
    categorizeObservations,
    createCommitmentStatuses,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createInitialCommitmentStatuses,
    createObservationStatuses,
    ensureCategoryOpenState,
    getCategoryAndId,
    reapplyIsOpenState,
    reCategorizeCommitments,
    reCategorizeObservations,
    setCommitmentLocationStatuses,
    setLocationActions,
    setObservationLocationStatuses,
    setStatuses,
    updateObservationCategoryOpenState,
} from './pillar-two.function';
import { defaultState, PillarTwoState } from './pillar-two.state';
import { TabRefId } from './tab-ref-id.model';

const { builder, reducer } = createReducer<ActionType, PillarTwoState>(
    defaultState(),
);

builder.add<SelectMetric>(
    ActionType.COACHING_DETAIL__P2__SELECT_METRIC,
    (state, action) => {
        const metricId = isNaN(action.payload.metricId)
            ? null
            : action.payload.metricId;
        const metricIdIsInMultiple = checkMetricIdInMultiple(
            metricId,
            state.commitmentGoalLookup,
        );

        // always re-categorize when metricId is null. see nonKpi in pillar-one
        const nextCommitmentCategories =
            metricId != null && metricId === state.metricId
                ? state.commitmentCategories
                : reCategorizeCommitments(state.commitmentCategories, metricId);
        const nextObservationCategories =
            metricId != null && metricId === state.metricId
                ? state.observationCategories
                : reCategorizeObservations(
                      state.observationCategories,
                      metricId,
                  );

        return {
            ...state,
            metricId,
            metricIdIsInMultiple,
            commitmentCategories: nextCommitmentCategories,
            observationCategories: nextObservationCategories,
            isFirstLoadFromOa: false,
        };
    },
);

builder
    .add<Reload>(ActionType.COACHING_DETAIL__P2__RELOAD, (state, action) => {
        return {
            ...state,
            pending: {
                ...state.pending,
                reload: true,
                reloadCommitments:
                    action.payload.which == null ||
                    action.payload.which === TabRefId.COMMITMENTS
                        ? true
                        : state.pending.reloadCommitments,
                reloadObservations:
                    action.payload.which == null ||
                    action.payload.which === TabRefId.OBSERVATIONS
                        ? true
                        : state.pending.reloadObservations,
            },
        };
    })
    .add<ReloadResult>(
        ActionType.COACHING_DETAIL__P2__RELOAD_RESULT,
        (state, action) => {
            let nextCmtStats = state.commitmentStatuses;
            let nextCmtCats = state.commitmentCategories;
            let nextLookup = state.commitmentGoalLookup;
            let metricIdIsInMultiple = state.metricIdIsInMultiple;

            if (
                action.payload.which == null ||
                action.payload.which === TabRefId.COMMITMENTS
            ) {
                const nextCommitments = action.payload.commitments;

                nextCmtCats = categorizeCommitments(
                    nextCommitments,
                    action.payload.authUserId,
                    state.metricId,
                    action.payload.commitmentConfig.goalThresholds,
                );
                nextCmtCats = setLocationActions(nextCmtCats, state.location);

                nextCmtStats = createCommitmentStatuses(nextCmtCats);
                nextCmtStats = setCommitmentLocationStatuses(
                    nextCmtStats,
                    state.location,
                );

                // TODO revise. logic is borrowed from pillar-one
                nextLookup = createCommitmentGoalLookup(nextCommitments);
                metricIdIsInMultiple = checkMetricIdInMultiple(
                    state.metricId,
                    nextLookup,
                );

                if (action.payload.retainIsOpen) {
                    reapplyIsOpenState(
                        state.commitmentCategories,
                        null,
                        nextCmtCats,
                        null,
                    );
                }
            }

            let nextObsStats = state.observationStatuses;
            let nextObsCats = state.observationCategories;

            if (
                action.payload.which == null ||
                action.payload.which === TabRefId.OBSERVATIONS
            ) {
                nextObsStats = createObservationStatuses(
                    action.payload.observations,
                );
                nextObsStats = setObservationLocationStatuses(
                    nextObsStats,
                    state.location,
                );

                nextObsCats = categorizeObservations(
                    action.payload.observations,
                    state.metricId,
                );

                if (action.payload.retainIsOpen) {
                    reapplyIsOpenState(
                        null,
                        state.observationCategories,
                        null,
                        nextObsCats,
                    );
                }
            }

            // if reapplyIsOpenState is being applied, ensureCategoryOpenState should always be called second
            const { category } = getCategoryAndId(
                nextCmtCats,
                nextObsCats,
                state.location.which,
                state.location.id,
            );
            [nextCmtCats, nextObsCats] = ensureCategoryOpenState(
                nextCmtCats,
                nextObsCats,
                category,
                true,
            );

            const nextReloadCommitments =
                action.payload.which == null ||
                action.payload.which === TabRefId.COMMITMENTS
                    ? false
                    : state.pending.reloadCommitments;
            const nextReloadObservations =
                action.payload.which == null ||
                action.payload.which === TabRefId.OBSERVATIONS
                    ? false
                    : state.pending.reloadObservations;

            const nextAllowedActions = determineAllowedActions();

            return {
                ...state,
                pending: {
                    ...state.pending,
                    reload:
                        !nextReloadCommitments && !nextReloadCommitments
                            ? false
                            : state.pending.reload,
                    reloadCommitments: nextReloadCommitments,
                    reloadObservations: nextReloadObservations,
                },
                allowedActions: nextAllowedActions,
                commitmentStatuses: nextCmtStats,
                observationStatuses: nextObsStats,
                commitmentGoalLookup: nextLookup,
                metricIdIsInMultiple,
                commitmentCategories: nextCmtCats,
                observationCategories: nextObsCats,
            };
        },
    )
    .add<ReloadCancel>(
        ActionType.COACHING_DETAIL__P2__RELOAD_CANCEL,
        (state) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    reload: false,
                },
            };
        },
    );

// TODO revise. this action might be called before COACHING_DETAIL__P2__RELOAD_RESULT is called for the first time
builder.add<SetActive>(
    ActionType.COACHING_DETAIL__P2__SET_ACTIVE,
    (state, action) => {
        const nextLocation: LocationView = {
            which: action.payload.which,
            id: action.payload.id,
        };
        let nextCmtStats = createCommitmentStatuses(state.commitmentCategories);
        nextCmtStats = setCommitmentLocationStatuses(
            nextCmtStats,
            nextLocation,
        );
        const nextObsStats = setObservationLocationStatuses(
            state.observationStatuses,
            nextLocation,
        );
        let nextCmtCats = setLocationActions(
            state.commitmentCategories,
            nextLocation,
        );
        let nextObsCats = state.observationCategories || [];

        const { category } = getCategoryAndId(
            nextCmtCats,
            nextObsCats,
            nextLocation.which,
            nextLocation.id,
        );
        [nextCmtCats, nextObsCats] = ensureCategoryOpenState(
            nextCmtCats,
            nextObsCats,
            category,
            true,
        );

        return {
            ...state,
            location: nextLocation,
            commitmentStatuses: nextCmtStats,
            observationStatuses: nextObsStats,
            commitmentCategories: nextCmtCats,
            observationCategories: nextObsCats,
            isFirstLoadFromOa: false,
        };
    },
);

builder.add<SetStatuses>(
    ActionType.COACHING_DETAIL__P2__SET_STATUSES,
    (state, action) => {
        const nextCmtStats = setCommitmentLocationStatuses(
            state.commitmentStatuses,
            state.location,
        );
        const nextObsStats = setObservationLocationStatuses(
            state.observationStatuses,
            state.location,
        );

        const [nextNextCmtStats, nextNextObsStats] = setStatuses(
            nextCmtStats,
            nextObsStats,
            state.location,
            state.commitmentCategories,
            action.payload.statuses,
        );

        let nextCmtCats = setLocationActions(
            state.commitmentCategories,
            state.location,
        );
        let nextObsCats = state.observationCategories;

        const { category } = getCategoryAndId(
            nextCmtCats,
            nextObsCats,
            state.location.which,
            state.location.id,
        );
        [nextCmtCats, nextObsCats] = ensureCategoryOpenState(
            nextCmtCats,
            nextObsCats,
            category,
            true,
        );

        return {
            ...state,
            commitmentStatuses: nextNextCmtStats,
            observationStatuses: nextNextObsStats,
            commitmentCategories: nextCmtCats,
            observationCategories: nextObsCats,
        };
    },
);

builder.add<SetCategoryOpenState>(
    ActionType.COACHING_DETAIL__P2__SET_CATEGORY_OPEN_STATE,
    (state, action) => {
        const [nextNextCmtCats, nextNextObsCats] = ensureCategoryOpenState(
            state.commitmentCategories,
            state.observationCategories,
            action.payload.category,
            action.payload.isOpen,
        );

        return {
            ...state,
            commitmentCategories: nextNextCmtCats,
            observationCategories: nextNextObsCats,
        };
    },
);

builder.add<SetCategoryOpenStateForObservations>(
    ActionType.COACHING_DETAIL__P2__SET_CATEGORY_OPEN_STATE_FROM_CMT_FOR_OBSERVATIONS,
    (state, action) => {
        const nextNextObsCats = updateObservationCategoryOpenState(
            state.observationCategories,
            action.payload.cmtIdentifier,
        );

        return {
            ...state,
            observationCategories: nextNextObsCats,
        };
    },
);

export const coachingDetailPillarTwoReducer = reducer;
